import React, { useEffect, useState } from 'react';
import { library } from 'components';
import { Widget } from 'models/WidgetModel';
import PropTypes from 'prop-types';
import WidgetToRender from 'components/Render/WidgetToRender';
import { useStore as useGlobalStore } from 'store/useGlobalStore';
import { WidgetProvider } from 'contexts/WidgetProvider';
import { useGetWidgetData } from 'hooks/useGetWidgetData';
import { isDiyWidgetEngineConfig } from 'utils/normalizeWidgetConfig';
import { hasRenderableWidgetData } from 'components/AiAnalytics/shared/aiWidgetGuards';
export interface CardWidgetProps {
  widget: Widget;
  index: number;
  removeWidget?: (id: number) => void;
  copyWidget?: (id: number) => void;
  previewMode?: boolean;
}

const CardWidget: React.FC<CardWidgetProps> = ({
  widget,
  index,
  removeWidget,
  copyWidget,
  previewMode = false,
}) => {
  const { url, payload, customWidgetConfig } = widget;

  const isDiyWidgetEngineByConfig = isDiyWidgetEngineConfig(customWidgetConfig);

  const [alias, setAlias] = useState<string>();
  const endpoints = useGlobalStore((state) => state.endpoints);
  const dataSourceType = useGlobalStore((state) => state.dataSourceType);
  const editing = useGlobalStore((state) => state.editing);
  const diyMode = useGlobalStore((state) => state.diyMode);
  const setState = useGlobalStore((state) => state.setState);
  const isDiyWidgetEngineByStore = useGlobalStore((state) => state.isDiyWidgetEngine);
  const customWidgetFromStore = useGlobalStore((state) => state.customWidget);
  const isDiyWidgetEngine = isDiyWidgetEngineByConfig || isDiyWidgetEngineByStore;
  const [dateFilterModel, setDateFilterModel] = useState<any>();
  const { data: response, error, getData, loading, waitingForSignalR } = useGetWidgetData();
  useEffect(() => {
    if (widget?.alias !== library.CUSTOM_WIDGET || widget?.alias !== library.QUERY_WIDGET) {
      setAlias(widget.alias);
    }
  }, [widget]);

  const savedEnterpriseWidgetId = useGlobalStore((state) => state.savedEnterpriseWidgetId);
  const isWidgetBeingEdited =
    (diyMode || (isDiyWidgetEngineByStore && editing)) &&
    ((customWidgetFromStore?.id || customWidgetFromStore?.config?.i) ===
      (widget?.id || widget?.config?.i) ||
      (savedEnterpriseWidgetId && savedEnterpriseWidgetId === (widget?.id || widget?.config?.i)));
  useEffect(() => {
    if (isDiyWidgetEngine && (previewMode || isWidgetBeingEdited)) return;
    if ((url || endpoints.getWidgetData) && alias && payload) {
      if (customWidgetConfig?.dataSource !== 'generic') {
        // Add the configured trend to the request payload when the widget defines one.
        const requestPayload =
          customWidgetConfig?.trend !== undefined
            ? { ...payload, trend: customWidgetConfig.trend }
            : payload;
        getData(requestPayload);
      }
    }
  }, [alias, payload, customWidgetConfig]);

  const validateData = (res: any) => {
    if (!res?.data) return false;

    // Preserve timeframe labels independently of renderability.
    const { dateFilterModel } = res;
    setDateFilterModel(dateFilterModel);

    return hasRenderableWidgetData(widget.alias, res);
  };

  const editWidget = (widget: any) => {
    setState({ customWidget: widget });
  };
  return (
    <WidgetProvider>
      <WidgetToRender
        {...{
          removeWidget,
          copyWidget,
          editWidget,
          widget,
          validateData,
          response,
          index,
          loading: loading || waitingForSignalR,
          error,
          dataSourceType,
          customWidgetConfig,
          previewMode,
          dateFilterModel,
        }}
      />
    </WidgetProvider>
  );
};

CardWidget.propTypes = {
  widget: PropTypes.any.isRequired,
};

export default CardWidget;
